/** XeAgents.ai **/
import { Card } from "@/components/ui/card";
import { AnalyticsChart } from "@/components/AnalyticsChart";
import { useTranslation } from "react-i18next";

interface Call {
  id: string;
  sentiment: string | null;
  classification: string | null;
}

interface SentimentAnalysisProps {
  calls: Call[];
}

export function SentimentAnalysis({ calls }: SentimentAnalysisProps) {
  const { t } = useTranslation();

  // Sentiment distribution
  const sentimentData = [
    {
      name: "Positive",
      value: calls.filter(c => c.sentiment === 'positive').length,
    },
    {
      name: "Neutral",
      value: calls.filter(c => c.sentiment === 'neutral').length,
    },
    {
      name: "Negative",
      value: calls.filter(c => c.sentiment === 'negative').length,
    },
  ].filter(item => item.value > 0);

  // Sentiment to Lead conversion correlation
  const sentimentCorrelation = ['positive', 'neutral', 'negative'].map(sentiment => {
    const sentimentCalls = calls.filter(c => c.sentiment === sentiment);
    const converted = sentimentCalls.filter(c =>
      c.classification === 'hot' || c.classification === 'warm'
    ).length;
    const rate = sentimentCalls.length > 0
      ? ((converted / sentimentCalls.length) * 100).toFixed(1)
      : "0";

    return {
      sentiment: sentiment.charAt(0).toUpperCase() + sentiment.slice(1),
      total: sentimentCalls.length,
      converted,
      rate: parseFloat(rate),
    };
  }).filter(item => item.total > 0);

  // Calculate insights
  const positiveCalls = calls.filter(c => c.sentiment === 'positive');
  const positiveConverted = positiveCalls.filter(c =>
    c.classification === 'hot' || c.classification === 'warm'
  ).length;
  const positiveConversionRate = positiveCalls.length > 0
    ? ((positiveConverted / positiveCalls.length) * 100).toFixed(1)
    : "0";

  const negativeCalls = calls.filter(c => c.sentiment === 'negative');
  const negativeConverted = negativeCalls.filter(c =>
    c.classification === 'hot' || c.classification === 'warm'
  ).length;
  const negativeConversionRate = negativeCalls.length > 0
    ? ((negativeConverted / negativeCalls.length) * 100).toFixed(1)
    : "0";

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold">Sentiment Analysis</h3>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Sentiment Distribution */}
        {sentimentData.length > 0 && (
          <AnalyticsChart
            title="Sentiment Distribution"
            type="pie"
            data={sentimentData}
            testId="chart-sentiment-distribution"
          />
        )}

        {/* Sentiment to Conversion Correlation */}
        {sentimentCorrelation.length > 0 && (
          <Card className="p-6">
            <h4 className="text-lg font-semibold mb-4">Sentiment → Lead Conversion</h4>
            <div className="space-y-4">
              {sentimentCorrelation.map((item) => (
                <div key={item.sentiment} className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">{item.sentiment}</span>
                    <span className="text-sm text-muted-foreground">
                      {item.converted}/{item.total} converted ({item.rate}%)
                    </span>
                  </div>
                  <div className="h-3 bg-muted rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-300 ${
                        item.sentiment === 'Positive'
                          ? 'bg-green-500'
                          : item.sentiment === 'Neutral'
                            ? 'bg-gray-500'
                            : 'bg-red-500'
                      }`}
                      style={{ width: `${item.rate}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Insight */}
            <div className="mt-6 p-4 bg-muted/50 rounded-lg">
              <p className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">Insight: </span>
                {parseFloat(positiveConversionRate) > parseFloat(negativeConversionRate) ? (
                  <>
                    Positive sentiment calls convert at <span className="text-green-600 font-semibold">{positiveConversionRate}%</span>,
                    which is <span className="font-semibold">{(parseFloat(positiveConversionRate) - parseFloat(negativeConversionRate)).toFixed(1)}%</span> higher
                    than negative sentiment calls.
                  </>
                ) : (
                  <>
                    Calls with positive sentiment have a <span className="text-green-600 font-semibold">{positiveConversionRate}%</span> conversion rate
                    to qualified leads.
                  </>
                )}
              </p>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
